import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../lib/permissions';
import { useTier } from '../context/TierContext';
import { FeatureGate } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DatePicker } from '../components/ui/date-picker';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { 
  Plus, Pencil, Trash2, Search, Shield, AlertTriangle, CheckCircle, Clock, 
  Upload, FileText, User, RefreshCw, MoreHorizontal, ChevronRight, Download,
  FileUp, X, Truck, Users
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Compliance = () => {
  const { api, user } = useAuth();
  const { hasFeature } = useTier();
  const [records, setRecords] = useState([]);
  const [operators, setOperators] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [complianceTypes, setComplianceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expiringData, setExpiringData] = useState({ expired: [], expiring_soon: [] });
  const [activeTab, setActiveTab] = useState('operator');
  const [tenantSettings, setTenantSettings] = useState({ date_format: 'DD/MM/YYYY' });
  
  // Format date according to tenant settings
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '-';
    const date = dateString.includes('T') ? new Date(dateString) : new Date(dateString + 'T00:00:00');
    const dateFormat = tenantSettings.date_format || 'DD/MM/YYYY';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[date.getMonth()];
    
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD-MMM-YYYY':
        return `${day}-${monthShort}-${year}`;
      default:
        return `${day}/${month}/${year}`;
    }
  };
  
  // Bulk import state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [importTemplate, setImportTemplate] = useState(null);
  const [parsedRecords, setParsedRecords] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Collapsed state for entity groups (all collapsed by default)
  const [expandedEntities, setExpandedEntities] = useState({});
  
  const [form, setForm] = useState({
    entity_type: 'operator',
    entity_id: '',
    compliance_type_id: '',
    sub_type_ids: [],
    reference_number: '',
    issue_date: '',
    expiry_date: '',
    document_url: '',
    document_name: '',
    notes: '',
    status: 'active'
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = async () => {
    try {
      const [recordsRes, operatorsRes, equipmentRes, suppliersRes, typesRes, expiringRes] = await Promise.all([
        api.get('/compliance'),
        api.get('/operators'),
        api.get('/equipment'),
        api.get('/suppliers'),
        api.get('/compliance-types'),
        api.get('/compliance/expiring?days=30')
      ]);
      setRecords(recordsRes.data);
      setOperators(operatorsRes.data);
      setEquipment(equipmentRes.data);
      setSuppliers(suppliersRes.data);
      setComplianceTypes(typesRes.data);
      setExpiringData(expiringRes.data);
      
      // Fetch tenant settings separately using client_id from user context
      if (user?.client_id) {
        try {
          const tenantRes = await api.get(`/tenants/${user.client_id}`);
          if (tenantRes.data) {
            setTenantSettings({ date_format: tenantRes.data.date_format || 'DD/MM/YYYY' });
          }
        } catch (tenantError) {
          // Tenant settings are optional, use defaults if not available
          console.warn('Could not fetch tenant settings, using defaults');
        }
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  // Update form entity_type when tab changes
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      entity_type: activeTab,
      entity_id: '',
      compliance_type_id: '',
      sub_type_ids: []
    }));
    setEntityFilter('all');
  }, [activeTab]);

  const resetForm = () => {
    setForm({
      entity_type: activeTab,
      entity_id: '',
      compliance_type_id: '',
      sub_type_ids: [],
      reference_number: '',
      issue_date: '',
      expiry_date: '',
      document_url: '',
      document_name: '',
      notes: '',
      status: 'active'
    });
    setEditingRecord(null);
  };

  const handleOpenDialog = (record = null) => {
    if (record) {
      setEditingRecord(record);
      // Handle legacy records that only have operator_id
      const entityType = record.entity_type || 'operator';
      const entityId = record.entity_id || record.operator_id;
      setForm({
        entity_type: entityType,
        entity_id: entityId,
        compliance_type_id: record.compliance_type_id,
        sub_type_ids: record.sub_type_ids || [],
        reference_number: record.reference_number || '',
        issue_date: record.issue_date || '',
        expiry_date: record.expiry_date || '',
        document_url: record.document_url || '',
        document_name: record.document_name || '',
        notes: record.notes || '',
        status: record.status || 'active'
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await api.post('/compliance/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm({
        ...form,
        document_url: response.data.url,
        document_name: response.data.filename
      });
      toast.success('Document uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!form.document_url) return;
    
    try {
      // Extract filename from URL
      let filename = '';
      if (form.document_url.includes('compliance/')) {
        const parts = form.document_url.split('compliance/');
        if (parts.length > 1) {
          // Get just the UUID.ext part, removing query params
          filename = 'compliance/' + parts[parts.length - 1].split('?')[0];
        }
      }
      
      if (process.env.NODE_ENV === 'development') console.log('Deleting file:', filename);
      
      if (filename) {
        // Encode slashes properly for the URL path
        const encodedFilename = filename.split('/').map(p => encodeURIComponent(p)).join('/');
        const response = await api.delete(`/compliance/files/${encodedFilename}`);
        if (process.env.NODE_ENV === 'development') console.log('Delete response:', response.data);
      }
      
      // Clear document from form
      setForm({
        ...form,
        document_url: '',
        document_name: ''
      });
      toast.success('Document deleted from storage');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate mandatory fields
    if (!form.entity_type) {
      toast.error('Please select an Entity Type');
      return;
    }
    if (!form.entity_id) {
      toast.error(`Please select ${form.entity_type === 'operator' ? 'a Worker' : form.entity_type === 'equipment' ? 'an Asset' : 'a Subcontractor'}`);
      return;
    }
    if (!form.compliance_type_id) {
      toast.error('Please select a Compliance Type');
      return;
    }
    const selType = complianceTypes.find(t => t.id === form.compliance_type_id);
    if (selType?.requires_expiry_date && !form.expiry_date) {
      toast.error('Expiry Date is required for this compliance type');
      return;
    }
    
    try {
      // For backward compatibility, also set operator_id when entity_type is operator
      const submitData = { ...form };
      if (form.entity_type === 'operator') {
        submitData.operator_id = form.entity_id;
      }
      
      if (editingRecord) {
        await api.put(`/compliance/${editingRecord.id}`, submitData);
        toast.success('Record updated');
      } else {
        await api.post('/compliance', submitData);
        toast.success('Record created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    const record = records.find(r => r.id === id);
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/compliance/${recordToDelete.id}`);
      toast.success('Record deleted');
      fetchData();
      setDeleteDialogOpen(false);
      setRecordToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper functions
  const getEntityName = (entityType, entityId) => {
    if (entityType === 'operator') {
      return operators.find(o => o.id === entityId)?.name || 'Unknown';
    } else if (entityType === 'equipment') {
      const eq = equipment.find(e => e.id === entityId);
      return eq ? `${eq.asset_id || eq.registration || eq.id}` : 'Unknown';
    } else if (entityType === 'supplier') {
      const sup = suppliers.find(s => s.id === entityId);
      return sup?.company_name || sup?.name || 'Unknown';
    }
    return 'Unknown';
  };
  
  const getEntityList = () => {
    if (activeTab === 'operator') return operators;
    if (activeTab === 'equipment') return equipment;
    if (activeTab === 'supplier') return suppliers;
    return [];
  };
  
  const getEntityLabel = (entity) => {
    if (activeTab === 'operator') return entity.name;
    if (activeTab === 'equipment') return entity.name || entity.registration || entity.id;
    if (activeTab === 'supplier') return entity.company_name || entity.name;
    return entity.id;
  };

  const getComplianceTypeName = (id, record) => complianceTypes.find(t => t.id === id)?.name || record?.compliance_type_name || 'Unknown';
  
  // Filter compliance types by entity type
  const filteredComplianceTypes = useMemo(() => {
    return complianceTypes.filter(t => {
      const appliesTo = t.applies_to || ['operator'];
      return appliesTo.includes(form.entity_type);
    });
  }, [complianceTypes, form.entity_type]);

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'unknown', label: 'No Expiry', color: 'secondary' };
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Expired', color: 'destructive', days: Math.abs(daysUntilExpiry) };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', label: `${daysUntilExpiry} days`, color: 'warning', days: daysUntilExpiry };
    } else {
      return { status: 'valid', label: 'Valid', color: 'success', days: daysUntilExpiry };
    }
  };

  const getStatusBadge = (expiryDate) => {
    const status = getExpiryStatus(expiryDate);
    
    if (status.status === 'expired') {
      return <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />Expired</Badge>;
    } else if (status.status === 'expiring') {
      return <Badge className="bg-amber-500/20 text-amber-500 hover:bg-amber-500/30 gap-1"><Clock className="h-3 w-3" />{status.label}</Badge>;
    } else if (status.status === 'valid') {
      return <Badge className="bg-green-500/20 text-green-500 hover:bg-green-500/30 gap-1"><CheckCircle className="h-3 w-3" />Valid</Badge>;
    }
    return <Badge variant="secondary">No Expiry</Badge>;
  };

  // Get selected compliance type with its sub-types
  const selectedComplianceType = complianceTypes.find(t => t.id === form.compliance_type_id);
  const availableSubTypes = selectedComplianceType?.sub_types || [];

  // Toggle a sub-type selection
  const toggleSubType = (subTypeId) => {
    setForm(prev => ({
      ...prev,
      sub_type_ids: prev.sub_type_ids.includes(subTypeId)
        ? prev.sub_type_ids.filter(id => id !== subTypeId)
        : [...prev.sub_type_ids, subTypeId]
    }));
  };

  // Get sub-type names for display
  const getSubTypeNames = (record) => {
    const compType = complianceTypes.find(t => t.id === record.compliance_type_id);
    if (!compType?.sub_types || !record.sub_type_ids?.length) return null;
    
    return record.sub_type_ids
      .map(id => compType.sub_types.find(st => st.id === id))
      .filter(Boolean)
      .map(st => st.code)
      .join(', ');
  };

  // Filter records by active tab (entity type)
  const tabFilteredRecords = useMemo(() => {
    return records.filter(record => {
      const entityType = record.entity_type || 'operator';
      return entityType === activeTab;
    });
  }, [records, activeTab]);

  // Further filter records
  const filteredRecords = tabFilteredRecords.filter(record => {
    const entityType = record.entity_type || 'operator';
    const entityId = record.entity_id || record.operator_id;
    const entityName = getEntityName(entityType, entityId);
    const compType = complianceTypes.find(t => t.id === record.compliance_type_id);
    const compTypeName = compType?.name || record.compliance_type_name || '';
    
    const matchesSearch = 
      entityName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      compTypeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.reference_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesEntity = entityFilter === 'all' || entityId === entityFilter;
    
    const expiryStatus = getExpiryStatus(record.expiry_date);
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'expired' && expiryStatus.status === 'expired') ||
      (statusFilter === 'expiring' && expiryStatus.status === 'expiring') ||
      (statusFilter === 'valid' && expiryStatus.status === 'valid');
    
    return matchesSearch && matchesEntity && matchesStatus;
  });

  // Group records by entity
  const groupedRecords = useMemo(() => {
    const groups = {};
    
    filteredRecords.forEach(record => {
      const entityId = record.entity_id || record.operator_id;
      if (!groups[entityId]) {
        const entityType = record.entity_type || 'operator';
        let entity;
        let displayName = 'Unknown';
        
        if (entityType === 'operator') {
          entity = operators.find(o => o.id === entityId);
          displayName = entity?.name || 'Unknown Worker';
        } else if (entityType === 'equipment') {
          entity = equipment.find(e => e.id === entityId);
          displayName = entity?.name || entity?.asset_id || 'Unknown Asset';
        } else if (entityType === 'supplier') {
          entity = suppliers.find(s => s.id === entityId);
          displayName = entity?.company_name || entity?.name || 'Unknown Subcontractor';
        }
        
        groups[entityId] = {
          entity: entity || { id: entityId },
          entityType: entityType,
          displayName: displayName,
          records: [],
          stats: { valid: 0, expiring: 0, expired: 0 }
        };
      }
      groups[entityId].records.push(record);
      
      // Update stats
      const status = getExpiryStatus(record.expiry_date);
      if (status.status === 'expired') groups[entityId].stats.expired++;
      else if (status.status === 'expiring') groups[entityId].stats.expiring++;
      else groups[entityId].stats.valid++;
    });
    
    // Sort groups by display name
    return Object.values(groups).sort((a, b) => {
      return a.displayName.localeCompare(b.displayName);
    });
  }, [filteredRecords, operators, equipment, suppliers]);

  // Toggle entity group expansion
  const toggleEntityGroup = (entityId) => {
    setExpandedEntities(prev => ({
      ...prev,
      [entityId]: !prev[entityId]
    }));
  };

  // Expand/collapse all
  const expandAll = () => {
    const allExpanded = {};
    groupedRecords.forEach(g => { allExpanded[g.entity.id] = true; });
    setExpandedEntities(allExpanded);
  };

  const collapseAll = () => {
    setExpandedEntities({});
  };

  // Get entity icon
  const getEntityIcon = (entityType) => {
    if (entityType === 'operator') return <User className="h-5 w-5 text-muted-foreground" />;
    if (entityType === 'equipment') return <Truck className="h-5 w-5 text-muted-foreground" />;
    if (entityType === 'supplier') return <Users className="h-5 w-5 text-muted-foreground" />;
    return <Shield className="h-5 w-5 text-muted-foreground" />;
  };

  // Count records per tab
  const getTabCounts = () => {
    const counts = { operator: 0, equipment: 0, supplier: 0 };
    records.forEach(r => {
      const entityType = r.entity_type || 'operator';
      if (counts[entityType] !== undefined) {
        counts[entityType]++;
      }
    });
    return counts;
  };
  const tabCounts = getTabCounts();

  // ========== BULK IMPORT FUNCTIONS ==========
  const handleOpenBulkDialog = async () => {
    try {
      const res = await api.get('/compliance/import-template');
      setImportTemplate(res.data);
      setParsedRecords([]);
      setImportErrors([]);
      setBulkDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load import template');
    }
  };

  const downloadCsvTemplate = () => {
    if (!importTemplate) return;
    
    // Create CSV content
    const headers = importTemplate.columns.map(c => c.name).join(',');
    const exampleRow = 'OPERATOR_CODE,Compliance Type Name,WP,REF-001,2025-01-01,2026-01-01,Notes here';
    
    // Add operators reference section
    let operatorsRef = '\n\n# OPERATORS REFERENCE (delete this section before import)\n';
    operatorsRef += '# Code,Name\n';
    importTemplate.operators.forEach(op => {
      operatorsRef += `# ${op.code},${op.name}\n`;
    });
    
    // Add compliance types reference
    let typesRef = '\n# COMPLIANCE TYPES REFERENCE (delete this section before import)\n';
    typesRef += '# Type Name,Available Sub-Types\n';
    importTemplate.compliance_types.forEach(ct => {
      const subTypes = ct.sub_types?.length ? ct.sub_types.join(',') : 'None';
      typesRef += `# ${ct.name},${subTypes}\n`;
    });
    
    const csvContent = headers + '\n' + exampleRow + operatorsRef + typesRef;
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'compliance_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => 
        line.trim() && !line.trim().startsWith('#')
      );
      
      if (lines.length < 2) {
        toast.error('CSV file must have a header row and at least one data row');
        return;
      }
      
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const records = [];
      const errors = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const record = {};
        
        headers.forEach((header, idx) => {
          record[header] = values[idx] || '';
        });
        
        // Basic validation
        const rowErrors = [];
        if (!record.operator_code) rowErrors.push('Missing operator_code');
        if (!record.compliance_type) rowErrors.push('Missing compliance_type');
        
        if (rowErrors.length > 0) {
          errors.push({ row: i + 1, errors: rowErrors, data: record });
        }
        
        records.push({ ...record, _row: i + 1, _errors: rowErrors });
      }
      
      setParsedRecords(records);
      setImportErrors(errors);
    };
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    const validRecords = parsedRecords.filter(r => r._errors.length === 0);
    if (validRecords.length === 0) {
      toast.error('No valid records to import');
      return;
    }
    
    setImporting(true);
    try {
      const payload = validRecords.map(r => ({
        operator_code: r.operator_code,
        compliance_type: r.compliance_type,
        sub_types: r.sub_types,
        reference_number: r.reference_number,
        issue_date: r.issue_date,
        expiry_date: r.expiry_date,
        notes: r.notes
      }));
      
      const res = await api.post('/compliance/bulk-import', payload);
      
      if (res.data.success > 0) {
        toast.success(`Successfully imported ${res.data.success} record(s)`);
      }
      if (res.data.errors?.length > 0) {
        toast.error(`${res.data.errors.length} record(s) failed to import`);
        setImportErrors(res.data.errors);
      } else {
        setBulkDialogOpen(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  const canWrite = hasPermission(user, 'compliance.write');

  return (
    <FeatureGate 
      feature="compliance"
      title="Compliance Management"
      description="Track and manage licences, certificates, and compliance records for operators, equipment, and suppliers with automatic expiry alerts."
    >
      <div className="space-y-6" data-testid="compliance-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold">Compliance</h1>
            <p className="text-muted-foreground">Manage licences, tickets, and certifications</p>
          </div>
          {canWrite && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleOpenBulkDialog} data-testid="bulk-upload-btn">
                <FileUp className="h-4 w-4 mr-2" />
                Bulk Upload
              </Button>
              <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-record-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Record
              </Button>
            </div>
          )}
        </div>

        {/* Alert Cards */}
        {(expiringData.expired_count > 0 || expiringData.expiring_soon_count > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {expiringData.expired_count > 0 && (
              <Card className="border-destructive/50 bg-destructive/10">
                <CardContent className="flex items-center gap-4 py-4">
                  <div className="p-3 rounded-full bg-destructive/20">
                    <AlertTriangle className="h-6 w-6 text-destructive" />
                  </div>
                <div>
                  <p className="font-semibold text-destructive">{expiringData.expired_count} Expired Items</p>
                  <p className="text-sm text-muted-foreground">Require immediate attention</p>
                </div>
              </CardContent>
            </Card>
          )}
          {expiringData.expiring_soon_count > 0 && (
            <Card className="border-amber-500/50 bg-amber-500/10">
              <CardContent className="flex items-center gap-4 py-4">
                <div className="p-3 rounded-full bg-amber-500/20">
                  <Clock className="h-6 w-6 text-amber-500" />
                </div>
                <div>
                  <p className="font-semibold text-amber-500">{expiringData.expiring_soon_count} Expiring Soon</p>
                  <p className="text-sm text-muted-foreground">Within the next 30 days</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="operator" className="gap-2" data-testid="tab-operator">
            <User className="h-4 w-4" />
            Workers
            <Badge variant="secondary" className="ml-1">{tabCounts.operator}</Badge>
          </TabsTrigger>
          <TabsTrigger value="equipment" className="gap-2" data-testid="tab-equipment">
            <Truck className="h-4 w-4" />
            Assets
            <Badge variant="secondary" className="ml-1">{tabCounts.equipment}</Badge>
          </TabsTrigger>
          <TabsTrigger value="supplier" className="gap-2" data-testid="tab-supplier">
            <Users className="h-4 w-4" />
            Subcontractors
            <Badge variant="secondary" className="ml-1">{tabCounts.supplier}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        <Card className="card-industrial mt-4">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search records..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="compliance-search"
                />
              </div>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={`All ${activeTab === 'operator' ? 'Operators' : activeTab === 'equipment' ? 'Assets' : 'Subcontractors'}`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All {activeTab === 'operator' ? 'Operators' : activeTab === 'equipment' ? 'Assets' : 'Subcontractors'}</SelectItem>
                  {getEntityList().map(entity => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {getEntityLabel(entity)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="expiring">Expiring Soon</SelectItem>
                  <SelectItem value="valid">Valid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Records Content - Same for all tabs */}
        <TabsContent value={activeTab} className="mt-0">
          <Card className="card-industrial">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  {activeTab === 'operator' ? 'Operator' : activeTab === 'equipment' ? 'Asset' : 'Subcontractor'} Compliance Records
                  <Badge variant="secondary" className="ml-2">{filteredRecords.length}</Badge>
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={expandAll}>
                    Expand All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={collapseAll}>
                    Collapse All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {groupedRecords.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No compliance records found</p>
                  {canWrite && (
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => handleOpenDialog()}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Record
                    </Button>
                  )}
                </div>
              ) : (
                groupedRecords.map((group) => (
                  <Collapsible 
                    key={group.entity.id}
                    open={expandedEntities[group.entity.id]}
                    onOpenChange={() => toggleEntityGroup(group.entity.id)}
                  >
                    <CollapsibleTrigger asChild>
                      <div 
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer border"
                        data-testid={`entity-group-${group.entity.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <ChevronRight 
                            className={`h-5 w-5 transition-transform ${expandedEntities[group.entity.id] ? 'rotate-90' : ''}`} 
                          />
                          {getEntityIcon(group.entityType)}
                          <div>
                            <span className="font-semibold">{group.displayName}</span>
                            <span className="text-muted-foreground ml-2">({group.records.length} record{group.records.length !== 1 ? 's' : ''})</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {group.stats.expired > 0 && (
                            <Badge variant="destructive" className="gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {group.stats.expired} expired
                            </Badge>
                          )}
                          {group.stats.expiring > 0 && (
                            <Badge className="bg-amber-500/20 text-amber-500 hover:bg-amber-500/30 gap-1">
                              <Clock className="h-3 w-3" />
                              {group.stats.expiring} expiring
                            </Badge>
                          )}
                          {group.stats.expired === 0 && group.stats.expiring === 0 && (
                            <Badge className="bg-green-500/20 text-green-500 hover:bg-green-500/30 gap-1">
                              <CheckCircle className="h-3 w-3" />
                              All valid
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-2 ml-8 border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="label-overline">Type</TableHead>
                              <TableHead className="label-overline">Reference</TableHead>
                              <TableHead className="label-overline">Expiry</TableHead>
                              <TableHead className="label-overline">Status</TableHead>
                              <TableHead className="label-overline">Document</TableHead>
                              {canWrite && <TableHead className="label-overline text-right">Actions</TableHead>}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.records.map((record) => (
                              <TableRow 
                                key={record.id} 
                                data-testid={`record-row-${record.id}`}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={(e) => {
                                  if (e.target.closest('button') || e.target.closest('a') || e.target.closest('[role="menuitem"]') || e.target.closest('[data-radix-collection-item]')) return;
                                  handleOpenDialog(record);
                                }}
                              >
                                <TableCell>
                                  <div>
                                    {getComplianceTypeName(record.compliance_type_id, record)}
                                    {getSubTypeNames(record) && (
                                      <span className="text-xs text-muted-foreground block">
                                        ({getSubTypeNames(record)})
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="font-mono text-sm">{record.reference_number || '-'}</TableCell>
                                <TableCell className="font-mono text-sm">
                                  {record.expiry_date ? formatDateDisplay(record.expiry_date) : '-'}
                                </TableCell>
                                <TableCell>{getStatusBadge(record.expiry_date)}</TableCell>
                                <TableCell>
                                  {record.document_url ? (
                                    <a 
                                      href={record.document_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-[#ffd108] hover:underline"
                                    >
                                      <FileText className="h-4 w-4" />
                                      View
                                    </a>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                {canWrite && (
                                  <TableCell className="text-right">
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0"
                                          data-testid={`compliance-actions-${record.id}`}
                                        >
                                          <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                          onClick={() => handleOpenDialog(record)}
                                          data-testid={`edit-compliance-${record.id}`}
                                        >
                                          <Pencil className="h-4 w-4 mr-2" />
                                          Edit Record
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          onClick={() => handleDelete(record.id)}
                                          className="text-destructive focus:text-destructive"
                                          data-testid={`delete-compliance-${record.id}`}
                                        >
                                          <Trash2 className="h-4 w-4 mr-2" />
                                          Delete Record
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Sheet (Flyout Panel) */}
      <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl">
              {editingRecord ? 'Edit Compliance Record' : 'Add Compliance Record'}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Entity Type <span className="text-red-500">*</span></Label>
                <Select 
                  value={form.entity_type} 
                  onValueChange={(v) => setForm({ ...form, entity_type: v, entity_id: '', compliance_type_id: '', sub_type_ids: [] })}
                  disabled={!!editingRecord}
                >
                  <SelectTrigger data-testid="entity-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operator">Operator</SelectItem>
                    <SelectItem value="equipment">Asset</SelectItem>
                    <SelectItem value="supplier">Subcontractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">
                  {form.entity_type === 'operator' ? 'Operator' : form.entity_type === 'equipment' ? 'Asset' : 'Subcontractor'} <span className="text-red-500">*</span>
                </Label>
                <Select value={form.entity_id} onValueChange={(v) => setForm({ ...form, entity_id: v })} required>
                  <SelectTrigger data-testid="entity-select">
                    <SelectValue placeholder={`Select ${form.entity_type === 'supplier' ? 'subcontractor' : form.entity_type}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {form.entity_type === 'operator' && operators.map(op => (
                      <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                    ))}
                    {form.entity_type === 'equipment' && equipment.map(eq => (
                      <SelectItem key={eq.id} value={eq.id}>{eq.asset_id || eq.registration || eq.id}</SelectItem>
                    ))}
                    {form.entity_type === 'supplier' && suppliers.map(sup => (
                      <SelectItem key={sup.id} value={sup.id}>{sup.company_name || sup.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Compliance Type <span className="text-red-500">*</span></Label>
              <Select 
                value={form.compliance_type_id} 
                onValueChange={(v) => setForm({ ...form, compliance_type_id: v, sub_type_ids: [] })} 
                required
              >
                <SelectTrigger data-testid="compliance-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {filteredComplianceTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filteredComplianceTypes.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No compliance types available for {form.entity_type}. Create one in Data Management.
                </p>
              )}
            </div>

            {/* Sub-Types Selection (conditional) */}
            {availableSubTypes.length > 0 && (
              <div className="space-y-3 p-4 rounded-lg bg-muted/50 border" data-testid="sub-types-section">
                <Label className="label-overline">
                  {selectedComplianceType?.name} Classes / Sub-Types
                </Label>
                <p className="text-sm text-muted-foreground">
                  Select the specific classes or competencies held
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                  {availableSubTypes.map((subType) => (
                    <div 
                      key={subType.id} 
                      className="flex items-start gap-2"
                      data-testid={`sub-type-${subType.id}`}
                    >
                      <Checkbox
                        id={`subtype-${subType.id}`}
                        checked={form.sub_type_ids.includes(subType.id)}
                        onCheckedChange={() => toggleSubType(subType.id)}
                        data-testid={`sub-type-checkbox-${subType.id}`}
                      />
                      <label 
                        htmlFor={`subtype-${subType.id}`}
                        className="text-sm cursor-pointer leading-tight"
                      >
                        <span className="font-medium">{subType.code}</span>
                        {subType.name && (
                          <span className="text-muted-foreground block text-xs">
                            {subType.name}
                          </span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
                {form.sub_type_ids.length > 0 && (
                  <p className="text-xs text-muted-foreground pt-2 border-t">
                    Selected: {form.sub_type_ids.length} class{form.sub_type_ids.length !== 1 ? 'es' : ''}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="label-overline">Reference Number</Label>
              <Input
                value={form.reference_number}
                onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                placeholder="e.g., LIC-12345"
                data-testid="reference-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Issue Date</Label>
                <DatePicker
                  value={form.issue_date}
                  onChange={(date) => setForm({ ...form, issue_date: date })}
                  formatDisplay={formatDateDisplay}
                  className="w-full"
                  testId="issue-date-input"
                  captionLayout="dropdown"
                  fromYear={new Date().getFullYear() - 50}
                  toYear={new Date().getFullYear() + 30}
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Expiry Date{selectedComplianceType?.requires_expiry_date && <span className="text-red-500"> *</span>}</Label>
                <DatePicker
                  value={form.expiry_date}
                  onChange={(date) => setForm({ ...form, expiry_date: date })}
                  formatDisplay={formatDateDisplay}
                  minDate={form.issue_date}
                  className="w-full"
                  testId="expiry-date-input"
                  captionLayout="dropdown"
                  fromYear={new Date().getFullYear() - 50}
                  toYear={new Date().getFullYear() + 30}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Document</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="document-upload"
                  data-testid="document-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('document-upload').click()}
                  disabled={uploading}
                  className="w-full"
                >
                  {uploading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {form.document_name || 'Upload Document'}
                </Button>
              </div>
              {form.document_url && (
                <div className="flex items-center justify-between bg-muted/50 rounded-md p-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm truncate">{form.document_name}</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteDocument}
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    data-testid="delete-document-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
                data-testid="notes-input"
              />
            </div>

            <SheetFooter className="pt-4 border-t mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} onMouseDown={() => document.activeElement?.blur()}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" onMouseDown={() => document.activeElement?.blur()}>
                {editingRecord ? 'Update' : 'Create'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <FileUp className="h-5 w-5" />
              Bulk Upload Compliance Records
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Step 1: Download Template */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <span className="bg-[#ffd108] text-black rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                Download Template
              </h3>
              <p className="text-sm text-muted-foreground">
                Download the CSV template which includes all operator codes and compliance type names for reference.
              </p>
              <Button variant="outline" onClick={downloadCsvTemplate} data-testid="download-template-btn">
                <Download className="h-4 w-4 mr-2" />
                Download CSV Template
              </Button>
            </div>

            {/* Step 2: Upload CSV */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <span className="bg-[#ffd108] text-black rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                Upload Completed CSV
              </h3>
              <p className="text-sm text-muted-foreground">
                Fill in the template and upload it here. Use exact operator codes and compliance type names.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  className="max-w-xs"
                  data-testid="csv-upload-input"
                />
              </div>
            </div>

            {/* Step 3: Preview */}
            {parsedRecords.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <span className="bg-[#ffd108] text-black rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                  Preview & Import
                </h3>
                
                <div className="flex items-center gap-4 text-sm">
                  <Badge className="bg-green-500/20 text-green-500 hover:bg-green-500/30">
                    {parsedRecords.filter(r => r._errors.length === 0).length} valid
                  </Badge>
                  {importErrors.length > 0 && (
                    <Badge variant="destructive">
                      {importErrors.length} with errors
                    </Badge>
                  )}
                </div>

                <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Row</TableHead>
                        <TableHead>Operator Code</TableHead>
                        <TableHead>Compliance Type</TableHead>
                        <TableHead>Sub-Types</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Expiry</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parsedRecords.map((record, idx) => (
                        <TableRow 
                          key={`compliance-${idx}`}
                          className={record._errors.length > 0 ? 'bg-destructive/10' : ''}
                        >
                          <TableCell className="font-mono text-sm">{record._row}</TableCell>
                          <TableCell>{record.operator_code}</TableCell>
                          <TableCell>{record.compliance_type}</TableCell>
                          <TableCell>{record.sub_types || '-'}</TableCell>
                          <TableCell>{record.reference_number || '-'}</TableCell>
                          <TableCell>{record.expiry_date || '-'}</TableCell>
                          <TableCell>
                            {record._errors.length > 0 ? (
                              <Badge variant="destructive" className="gap-1">
                                <X className="h-3 w-3" />
                                {record._errors.join(', ')}
                              </Badge>
                            ) : (
                              <Badge className="bg-green-500/20 text-green-500 hover:bg-green-500/30 gap-1">
                                <CheckCircle className="h-3 w-3" />
                                Valid
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Server-side errors after import attempt */}
                {importErrors.length > 0 && importErrors[0]?.errors && (
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                    <p className="font-semibold text-destructive mb-2">Import Errors:</p>
                    <ul className="text-sm space-y-1">
                      {importErrors.map((err, idx) => (
                        <li key={`detail-${idx}`}>
                          Row {err.row}: {err.errors.join(', ')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} onMouseDown={() => document.activeElement?.blur()}>
              Cancel
            </Button>
            <Button 
              className="btn-volt"
              onClick={handleBulkImport}
              disabled={importing || parsedRecords.filter(r => r._errors.length === 0).length === 0}
              data-testid="import-btn"
              onMouseDown={() => document.activeElement?.blur()}
            >
              {importing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import {parsedRecords.filter(r => r._errors.length === 0).length} Record(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Compliance Record"
        description="Are you sure you want to delete this compliance record? This action cannot be undone."
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        testId="delete-compliance-dialog"
      />
      </div>
    </FeatureGate>
  );
};

export default Compliance;
